/**
 * Shared Firestore service utilities
 * Provides helper functions for data transformation and Firestore operations
 */

/**
 * Normalize a date value to ensure it's a Unix date in milliseconds
 * Since all dates are stored as integers, this just returns the value as-is
 * @param {number | null} date - Unix date in milliseconds (integer)
 * @returns {number | null} Unix date in milliseconds, or null
 */
export const normalizeDate = (date) => {
  if (date === null || date === undefined) return null;
  // Ensure it's a number (dates are stored as integers)
  return typeof date === 'number' ? date : null;
};

/**
 * Convert snake_case object keys to camelCase
 * @param {Object} obj - Object with snake_case keys
 * @returns {Object} Object with camelCase keys
 */
export const snakeToCamel = (obj) => {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) {
    return obj.map(item => snakeToCamel(item));
  }
  if (typeof obj !== 'object') return obj;

  const converted = {};
  for (const [key, value] of Object.entries(obj)) {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

    // Recursively convert nested objects and arrays
    if (value !== null && value !== undefined) {
      if (Array.isArray(value) || typeof value === 'object') {
        converted[camelKey] = snakeToCamel(value);
      } else {
        converted[camelKey] = value;
      }
    } else {
      converted[camelKey] = value;
    }
  }
  return converted;
};

/**
 * Convert camelCase object keys to snake_case
 * @param {Object} obj - Object with camelCase keys
 * @returns {Object} Object with snake_case keys
 */
export const camelToSnake = (obj) => {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) {
    return obj.map(item => camelToSnake(item));
  }
  if (typeof obj !== 'object') return obj;

  const converted = {};
  for (const [key, value] of Object.entries(obj)) {
    // Skip undefined values - Firestore doesn't accept them
    if (value === undefined) continue;

    const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);

    // Recursively convert nested objects and arrays
    if (value && typeof value === 'object') {
      converted[snakeKey] = camelToSnake(value);
    } else {
      converted[snakeKey] = value;
    }
  }
  return converted;
};

/**
 * Transform Firestore document data to JavaScript model
 * Converts snake_case to camelCase and normalizes date fields
 * @param {string} docId - Document ID
 * @param {Object} data - Firestore document data
 * @returns {Object} Transformed data with camelCase keys and normalized dates
 */
export const transformDocument = (docId, data) => {
  if (!data) return null;

  const transformed = snakeToCamel(data);

  // Normalize date fields to ensure they're Unix dates in milliseconds
  const dateFields = ['dateCreated', 'dateUpdated', 'lastUpdated'];
  dateFields.forEach(field => {
    if (transformed[field]) {
      transformed[field] = normalizeDate(transformed[field]);
    }
  });

  return {
    id: docId,
    ...transformed,
  };
};
